import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface GalleryImage {
  id: string;
  url: string;
  title: string;
  event_date: string;
}

export default function GalleryPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [filter, setFilter] = useState<'all' | 'recent' | 'archive'>('all');

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchGalleryImages();
  }, []);

  const fetchGalleryImages = async () => {
    try {
      const { data, error } = await supabase
        .from('event_reports')
        .select('id, title, event_date, images')
        .order('event_date', { ascending: false });

      if (error) throw error;

      const allImages: GalleryImage[] = [];
      data?.forEach((report) => {
        if (report.images && Array.isArray(report.images)) {
          report.images.forEach((imageUrl: string) => {
            allImages.push({
              id: `${report.id}-${imageUrl}`,
              url: imageUrl,
              title: report.title,
              event_date: report.event_date,
            });
          });
        }
      });

      setImages(allImages);
    } catch (error) {
      console.error('画像の取得に失敗しました:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
  };

  const getFilteredImages = () => {
    const now = new Date();
    const sixMonthsAgo = new Date(now.setMonth(now.getMonth() - 6));

    switch (filter) {
      case 'recent':
        return images.filter(img => new Date(img.event_date) >= sixMonthsAgo);
      case 'archive':
        return images.filter(img => new Date(img.event_date) < sixMonthsAgo);
      default:
        return images;
    }
  };

  const filteredImages = getFilteredImages();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'}`}>
        <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-4 md:py-6 px-4 md:px-6">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
            <div>
              <h1 className="text-2xl md:text-4xl font-bold mb-1 tracking-wide" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                <Link to="/">兵庫中央岳風会</Link>
              </h1>
              <p className="text-xs md:text-sm opacity-90 font-light">公益社団法人日本詩吟学院認可</p>
            </div>
            <div className="text-left md:text-right text-xs md:text-sm font-light">
              <p className="flex items-center gap-2">
                <i className="ri-map-pin-line"></i>
                〒653-0867
              </p>
              <p className="mt-1">神戸市長田区高東町1-2-26</p>
            </div>
          </div>
        </div>
        <nav className="bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <ul className="flex flex-wrap justify-center items-center gap-1 md:gap-6 py-3 md:py-4">
              <li><Link to="/" className="text-gray-800 hover:text-blue-800 font-medium text-xs md:text-sm transition-all hover:scale-105 px-2 md:px-3 py-1 rounded-md whitespace-nowrap">トップページ</Link></li>
              <li><Link to="/about" className="text-gray-800 hover:text-blue-800 font-medium text-xs md:text-sm transition-all hover:scale-105 px-2 md:px-3 py-1 rounded-md whitespace-nowrap">会の紹介</Link></li>
              <li><Link to="/greeting" className="text-gray-800 hover:text-blue-800 font-medium text-xs md:text-sm transition-all hover:scale-105 px-2 md:px-3 py-1 rounded-md whitespace-nowrap">会長挨拶</Link></li>
              <li><Link to="/activities" className="text-gray-800 hover:text-blue-800 font-medium text-xs md:text-sm transition-all hover:scale-105 px-2 md:px-3 py-1 rounded-md whitespace-nowrap">活動計画</Link></li>
              <li><Link to="/membership" className="text-gray-800 hover:text-blue-800 font-medium text-xs md:text-sm transition-all hover:scale-105 px-2 md:px-3 py-1 rounded-md whitespace-nowrap">入会案内</Link></li>
              <li><Link to="/records" className="text-gray-800 hover:text-blue-800 font-medium text-xs md:text-sm transition-all hover:scale-105 px-2 md:px-3 py-1 rounded-md whitespace-nowrap">活動記録</Link></li>
              <li><Link to="/gallery" className="text-blue-800 font-bold border-b-2 border-blue-800 pb-1 text-xs md:text-sm px-2 md:px-3 py-1 whitespace-nowrap">ギャラリー</Link></li>
            </ul>
          </div>
        </nav>
      </header>

      {/* Breadcrumb */}
      <div className="pt-32 md:pt-40 bg-gradient-to-b from-gray-50 to-white py-4">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center space-x-2 text-xs md:text-sm text-gray-600">
            <Link to="/" className="hover:text-blue-800 whitespace-nowrap">トップ</Link>
            <span>›</span>
            <span>ギャラリー</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="py-8 md:py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <header className="mb-8 md:mb-12 text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-blue-900 mb-4" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              ギャラリー
            </h1>
            <p className="text-sm md:text-base text-gray-600 max-w-2xl mx-auto">
              兵庫中央岳風会の活動の様子を写真でご紹介します。
            </p>
          </header>

          {/* Filter Buttons */}
          <div className="flex justify-center gap-3 mb-8 md:mb-12">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 md:px-6 py-2 md:py-3 rounded-lg font-medium text-xs md:text-sm transition-all whitespace-nowrap ${
                filter === 'all'
                  ? 'bg-blue-800 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <i className="ri-image-line mr-2"></i>
              すべて
            </button>
            <button
              onClick={() => setFilter('recent')}
              className={`px-4 md:px-6 py-2 md:py-3 rounded-lg font-medium text-xs md:text-sm transition-all whitespace-nowrap ${
                filter === 'recent'
                  ? 'bg-blue-800 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <i className="ri-time-line mr-2"></i>
              最近の活動
            </button>
            <button
              onClick={() => setFilter('archive')}
              className={`px-4 md:px-6 py-2 md:py-3 rounded-lg font-medium text-xs md:text-sm transition-all whitespace-nowrap ${
                filter === 'archive'
                  ? 'bg-blue-800 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <i className="ri-archive-line mr-2"></i>
              アーカイブ
            </button>
          </div>

          {/* Gallery Grid */}
          {loading ? (
            <div className="text-center py-12 md:py-16">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <i className="ri-loader-4-line text-3xl md:text-5xl text-blue-800 animate-spin"></i>
              </div>
              <p className="text-gray-600 text-sm md:text-base">読み込み中...</p>
            </div>
          ) : filteredImages.length === 0 ? (
            <div className="text-center py-12 md:py-16">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-image-line text-3xl md:text-5xl text-blue-800"></i>
              </div>
              <p className="text-gray-600 text-sm md:text-base mb-2">現在、写真はありません。</p>
              <p className="text-xs md:text-sm text-gray-500">イベント報告に写真を追加すると、ここに表示されます。</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {filteredImages.map((image) => (
                  <div
                    key={image.id}
                    className="group relative aspect-square rounded-lg overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer"
                    onClick={() => setSelectedImage(image)}
                  >
                    <img
                      src={image.url}
                      alt={image.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4">
                        <p className="text-white text-xs md:text-sm font-bold mb-1 line-clamp-2">{image.title}</p>
                        <p className="text-white/80 text-xs">{formatDate(image.event_date)}</p>
                      </div>
                    </div>
                    <div className="absolute top-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <i className="ri-zoom-in-line text-blue-800"></i>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 text-center text-sm text-gray-600">
                <p>全 {filteredImages.length} 枚の写真</p>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 md:w-12 md:h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-all whitespace-nowrap"
            onClick={() => setSelectedImage(null)}
          >
            <i className="ri-close-line text-2xl md:text-3xl"></i>
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedImage.url}
              alt={selectedImage.title}
              className="w-full h-auto max-h-[80vh] object-contain rounded-lg shadow-2xl"
            />
            <div className="mt-4 text-center">
              <h3 className="text-white text-lg md:text-xl font-bold mb-2">{selectedImage.title}</h3>
              <p className="text-white/70 text-sm">{formatDate(selectedImage.event_date)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-10 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-8 md:mb-10 text-center">
            <h3 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: "'Noto Serif JP', serif" }}>兵庫中央岳風会</h3>
            <p className="text-xs md:text-sm opacity-80">公益社団法人日本詩吟学院認可</p>
          </div>
          
          <nav className="mb-8 md:mb-10">
            <ul className="flex flex-wrap justify-center gap-3 md:gap-6">
              <li><Link to="/" className="hover:text-gray-300 transition-colors text-xs md:text-sm whitespace-nowrap hover:underline">トップページ</Link></li>
              <li><Link to="/about" className="hover:text-gray-300 transition-colors text-xs md:text-sm whitespace-nowrap hover:underline">会の紹介</Link></li>
              <li><Link to="/greeting" className="hover:text-gray-300 transition-colors text-xs md:text-sm whitespace-nowrap hover:underline">会長挨拶</Link></li>
              <li><Link to="/activities" className="hover:text-gray-300 transition-colors text-xs md:text-sm whitespace-nowrap hover:underline">活動計画</Link></li>
              <li><Link to="/membership" className="hover:text-gray-300 transition-colors text-xs md:text-sm whitespace-nowrap hover:underline">入会案内</Link></li>
              <li><Link to="/records" className="hover:text-gray-300 transition-colors text-xs md:text-sm whitespace-nowrap hover:underline">活動記録</Link></li>
              <li><Link to="/gallery" className="hover:text-gray-300 transition-colors text-xs md:text-sm whitespace-nowrap hover:underline">ギャラリー</Link></li>
            </ul>
          </nav>
          
          <div className="text-center text-xs md:text-sm opacity-90 border-t border-white/20 pt-6 md:pt-8">
            <p className="mb-2">© 2025 兵庫中央岳風会. All rights reserved.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-gray-300 transition-colors whitespace-nowrap hover:underline">Powered by Readdy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
